import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/api';
import Dashboard from './Dashboard';
import ExpensesList from './ExpensesList';
import UsersList from './UsersList';
import GroupDetailView from './GroupDetailView';
import AddExpenseModal from './AddExpenseModal';
import SettleUpModal from './SettleUpModal';
import CreateGroupModal from './CreateGroupModal';

function MainLayout() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false);
  const [balances, setBalances] = useState({
    netBalance: 0,
    totalOwedFromOthers: 0,
    totalOwedToOthers: 0,
    balancesPerUser: []
  });
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);
  const [expenseToEdit, setExpenseToEdit] = useState(null);
  const [isSettleOpen, setIsSettleOpen] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null); // for quick settlement
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchDelta, setTouchDelta] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isPinned, setIsPinned] = useState(true);

  const fetchData = async () => {
    try {
      // Fetch users
      const usersRes = await apiFetch('/users');
      const usersData = await usersRes.json();
      setUsers(usersData);

      // Fetch groups
      const groupsRes = await apiFetch('/groups');
      const groupsData = await groupsRes.json();
      setGroups(groupsData);

      if (selectedGroup) {
        // Fetch group expenses
        const expensesRes = await apiFetch(`/groups/${selectedGroup.id}/expenses`);
        const expensesData = await expensesRes.json();
        setExpenses(expensesData.reverse());

        // Fetch group balances
        const balancesRes = await apiFetch(`/groups/${selectedGroup.id}/balances`);
        const balancesData = await balancesRes.json();
        setBalances(balancesData);
      } else {
        // Fetch global expenses
        const expensesRes = await apiFetch('/expenses');
        const expensesData = await expensesRes.json();
        setExpenses(expensesData.reverse()); // latest first

        // Fetch global balances
        const balancesRes = await apiFetch('/expenses/balances');
        const balancesData = await balancesRes.json();
        setBalances(balancesData);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  // Sync selectedGroup when groups list updates
  useEffect(() => {
    if (selectedGroup) {
      const freshGroup = groups.find((g) => g.id === selectedGroup.id);
      if (freshGroup && JSON.stringify(freshGroup) !== JSON.stringify(selectedGroup)) {
        setSelectedGroup(freshGroup);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Body scroll lock when drawer open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('drawer-open');
    } else {
      document.body.classList.remove('drawer-open');
    }
  }, [isMenuOpen]);

  // Escape key to close drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Touch gesture handlers for hamburger drawer
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const clientX = touch.clientX;
    const clientY = touch.clientY;

    if (!isMenuOpen) {
      // Opening: start only if within left edge (30px)
      if (clientX <= 30) {
        setTouchStartX(clientX);
        setTouchStartY(clientY);
        setIsDragging(true);
      }
    } else {
      // Closing: start only if within drawer width (280px)
      if (clientX <= 280) {
        setTouchStartX(clientX);
        setTouchStartY(clientY);
        setIsDragging(true);
      }
    }
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    const currentX = touch.clientX;
    const currentY = touch.clientY;
    const deltaX = currentX - touchStartX;
    const deltaY = currentY - touchStartY;

    // If vertical movement is dominant and exceeds 10px, cancel dragging (allow scroll)
    if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 10) {
      setIsDragging(false);
      return;
    }

    // Once horizontal intent clear, prevent vertical scroll
    if (Math.abs(deltaX) > 5) {
      e.preventDefault();
    }

    setTouchDelta(deltaX);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    const threshold = 100;

    if (isMenuOpen) {
      // Closing: require leftward swipe (negative delta) beyond threshold
      if (touchDelta < -threshold) {
        setIsMenuOpen(false);
      }
    } else {
      // Opening: require rightward swipe (positive delta) beyond threshold
      if (touchDelta > threshold) {
        setIsMenuOpen(true);
      }
    }

    setIsDragging(false);
    setTouchStartX(null);
    setTouchStartY(null);
    setTouchDelta(0);
  };

  // Compute sidebar style for dragging
  const getSidebarStyle = () => {
    if (isDragging) {
      if (isMenuOpen) {
        // Closing: start from 0, move left (negative)
        const offset = Math.max(Math.min(touchDelta, 0), -280);
        return {
          transform: `translateX(${offset}px)`,
          transition: 'none'
        };
      } else {
        // Opening: start from -280, move right (positive)
        const offset = Math.min(Math.max(touchDelta, 0), 280);
        return {
          transform: `translateX(calc(-280px + ${offset}px))`,
          transition: 'none'
        };
      }
    }
    return {};
  };

  return (
    <div className="app-layout" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {/* Sidebar Navigation */}
      <aside className={`sidebar ${isMenuOpen ? 'mobile-open' : ''} ${isPinned ? 'pinned' : ''}`} style={getSidebarStyle()}>
        <div className="sidebar-brand">
          <span className="brand-logo">💸</span>
          <h2>Private Split</h2>
          <button 
            className="pin-btn d-none-mobile"
            onClick={() => setIsPinned(!isPinned)}
            title={isPinned ? "Unpin Sidebar" : "Pin Sidebar"}
          >
            {isPinned ? '📌' : '📍'}
          </button>
        </div>

        <div className="user-profile-section">
          <div className="user-avatar">{getInitials(user.name)}</div>
          <div className="user-info">
            <span className="user-name">{user.name}</span>
            <span className="user-email">{user.email}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${(!selectedGroup && activeTab === 'dashboard') ? 'active' : ''}`}
            onClick={() => {
              setSelectedGroup(null);
              setActiveTab('dashboard');
            }}
          >
            📊 Dashboard
          </button>
          <button
            className={`nav-item ${(!selectedGroup && activeTab === 'expenses') ? 'active' : ''}`}
            onClick={() => {
              setSelectedGroup(null);
              setActiveTab('expenses');
            }}
          >
            🧾 Expenses Log
          </button>
          <button
            className={`nav-item ${(!selectedGroup && activeTab === 'users') ? 'active' : ''}`}
            onClick={() => {
              setSelectedGroup(null);
              setActiveTab('users');
            }}
          >
            👥 All Users
          </button>

          <div className="sidebar-section-header">
            <span>📂 Groups</span>
            <button className="btn-add-group-sidebar" onClick={() => setIsCreateGroupOpen(true)} title="Create Group">
              ➕
            </button>
          </div>
          <div className="sidebar-group-list">
            <button
              className={`nav-item ${selectedGroup === null ? 'active-global' : ''}`}
              onClick={() => setSelectedGroup(null)}
            >
              🌐 All Expenses
            </button>
            {groups.map((g) => (
              <button
                key={g.id}
                className={`nav-item group-item ${selectedGroup?.id === g.id ? 'active' : ''}`}
                onClick={() => setSelectedGroup(g)}
              >
                <span className="group-type-icon">{g.type === 'trip' ? '🧳' : '📅'}</span>
                <span className="group-item-name">{g.name}</span>
              </button>
            ))}
          </div>
        </nav>

        <button className="logout-btn" onClick={logout}>
          🚪 Sign Out
        </button>
      </aside>
      {isMenuOpen && <div className="sidebar-overlay active" onClick={() => setIsMenuOpen(false)} />}

      {/* Main Content Area */}
      <main className="content-area">
        {selectedGroup ? (
          <GroupDetailView
            group={selectedGroup}
            balances={balances}
            users={users}
            expenses={expenses}
            currentUser={user}
            onUpdateGroup={(updatedGroup) => {
              setSelectedGroup(updatedGroup);
              fetchData();
            }}
            onDeleteGroup={() => {
              setSelectedGroup(null);
              fetchData();
            }}
            onAddExpenseClick={() => setIsAddExpenseOpen(true)}
            onSettleUpClick={() => setIsSettleOpen(true)}
            onSettleUp={(friend) => {
              setSelectedFriend(friend);
              setIsSettleOpen(true);
            }}
            onDeleteExpense={async (id) => {
              if (window.confirm('Are you sure you want to delete this expense?')) {
                const res = await apiFetch(`/expenses/${id}`, { method: 'DELETE' });
                if (res.status === 403) { window.alert('You can only delete expenses you paid or participated in.'); return; }
                if (res.status === 404) { window.alert('This expense no longer exists.'); }
                fetchData();
              }
            }}
            onEditExpense={(expense) => {
              setExpenseToEdit(expense);
              setIsAddExpenseOpen(true);
            }}
            onDataChanged={fetchData}
            isMenuOpen={isMenuOpen}
            onToggleMenu={() => setIsMenuOpen(prev => !prev)}
            isPinned={isPinned}
          />
        ) : (
          <>
            <header className="content-header">
              <button
                className={`hamburger-btn ${isPinned ? 'd-none-desktop' : ''}`}
                onClick={() => setIsMenuOpen(prev => !prev)}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '24px', cursor: 'pointer', marginRight: '12px' }}
              >
                {isMenuOpen ? '✕' : '☰'}
              </button>
              <div className="header-title">
                <h1>{activeTab === 'expenses' ? 'Expenses Log' : activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
                <p>Welcome back, {user.name}!</p>
              </div>
              <div className="header-actions">
                <button className="btn btn-secondary" onClick={() => setIsSettleOpen(true)}>
                  🤝 Settle Up
                </button>
                <button className="btn btn-primary" onClick={() => setIsAddExpenseOpen(true)}>
                  ➕ Add Expense
                </button>
              </div>
            </header>

            {activeTab === 'dashboard' && (
              <Dashboard
                balances={balances}
                users={users}
                expenses={expenses}
                user={user}
                onSettleUp={(friend) => {
                  setSelectedFriend(friend);
                  setIsSettleOpen(true);
                }}
                onDeleteExpense={async (id) => {
                  if (window.confirm('Are you sure you want to delete this expense?')) {
                    const res = await apiFetch(`/expenses/${id}`, { method: 'DELETE' });
                    if (res.status === 403) { window.alert('You can only delete expenses you paid or participated in.'); return; }
                    if (res.status === 404) { window.alert('This expense no longer exists.'); }
                    fetchData();
                  }
                }}
              />
            )}

            {activeTab === 'expenses' && (
              <ExpensesList
                expenses={expenses}
                users={users}
                groups={groups}
                currentUser={user}
                onDeleteExpense={async (id) => {
                  if (window.confirm('Are you sure you want to delete this expense?')) {
                    const res = await apiFetch(`/expenses/${id}`, { method: 'DELETE' });
                    if (res.status === 403) { window.alert('You can only delete expenses you paid or participated in.'); return; }
                    if (res.status === 404) { window.alert('This expense no longer exists.'); }
                    fetchData();
                  }
                }}
                onEditExpense={(expense) => {
                  setExpenseToEdit(expense);
                  setIsAddExpenseOpen(true);
                }}
              />
            )}

            {activeTab === 'users' && <UsersList users={users} currentUser={user} />}
          </>
        )}
      </main>

      {/* Add Expense Modal */}
      {isAddExpenseOpen && (
        <AddExpenseModal
          isOpen={isAddExpenseOpen}
          onClose={() => {
            setIsAddExpenseOpen(false);
            setExpenseToEdit(null);
          }}
          allUsers={users}
          groups={groups}
          currentUser={user}
          initialGroupId={selectedGroup?.id || ''}
          expenseToEdit={expenseToEdit}
          onSuccess={fetchData}
        />
      )}

      {/* Settle Up Modal */}
      {isSettleOpen && (
        <SettleUpModal
          isOpen={isSettleOpen}
          onClose={() => {
            setIsSettleOpen(false);
            setSelectedFriend(null);
          }}
          users={selectedGroup ? users.filter((u) => selectedGroup.members.some((m) => m.userId === u.id)) : users}
          currentUser={user}
          balances={balances}
          preselectedFriend={selectedFriend}
          groupId={selectedGroup?.id}
          onSuccess={fetchData}
        />
      )}

      {/* Create Group Modal */}
      {isCreateGroupOpen && (
        <CreateGroupModal
          isOpen={isCreateGroupOpen}
          onClose={() => setIsCreateGroupOpen(false)}
          users={users}
          currentUser={user}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
}

export default MainLayout;
